import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Trash2, RefreshCw, Search, X, ChevronDown } from 'lucide-react';
import api from '../api/client';
import './ServerLogs.css';

interface LogEntry {
  timestamp: number;
  level: 'info' | 'error' | 'warn' | 'debug';
  message: string;
}

interface ServerLogsProps {
  serverId: string;
  isVisible?: boolean;
}

const getLogSeverity = (message: string, fallback: LogEntry['level']) => {
  const severityMatch = message.match(/\[(INFO|WARN|WARNING|ERROR|DEBUG)\]/i);
  if (severityMatch) {
    return severityMatch[1].toLowerCase();
  }

  return fallback.toLowerCase();
};

const getLogType = (message: string) => {
  const typeMatch = message.match(/\[(server|network|backend|client|race|match|game|auth|chat|telemetry)\]/i);
  if (typeMatch) {
    return typeMatch[1].toLowerCase();
  }

  return 'other';
};

const isHeartbeatNoise = (log: LogEntry) => {
  const message = log.message.toLowerCase();

  if (message.includes('lastheartbeat')) {
    return true;
  }

  if (message.includes('outbound msg to backend')) {
    const successMatch = log.message.match(/success:\s*(true|false)/i);
    if (!successMatch || successMatch[1].toLowerCase() === 'true') {
      return true;
    }
  }

  return false;
};

const ServerLogs: React.FC<ServerLogsProps> = ({ serverId, isVisible = true }) => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [autoScroll, setAutoScroll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedLogTypes, setSelectedLogTypes] = useState<string[]>([]);
  const [selectedSeverities, setSelectedSeverities] = useState<string[]>([]);
  const [typeFilterMode, setTypeFilterMode] = useState<'include' | 'exclude'>('include');
  const [severityFilterMode, setSeverityFilterMode] = useState<'include' | 'exclude'>('include');
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const [isSeverityDropdownOpen, setIsSeverityDropdownOpen] = useState(false);
  const logsEndRef = useRef<HTMLDivElement>(null);
  const selectedLogRef = useRef<HTMLDivElement | null>(null);
  const typeDropdownRef = useRef<HTMLDivElement>(null);
  const severityDropdownRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (autoScroll) {
      scrollToBottom();
    }
  }, [logs, autoScroll]);

  const availableLogTypes = useMemo(() => {
    const types = new Set<string>();
    logs.forEach((log) => types.add(getLogType(log.message)));
    return Array.from(types).sort();
  }, [logs]);

  const availableSeverities = useMemo(() => {
    const severities = new Set<string>();
    logs.forEach((log) => severities.add(getLogSeverity(log.message, log.level)));
    return Array.from(severities).sort();
  }, [logs]);

  const lastHeartbeat = useMemo(() => {
    const heartbeatEntries = logs.filter((log) => log.message.toLowerCase().includes('lastheartbeat'));
    if (heartbeatEntries.length === 0) {
      return null;
    }

    return heartbeatEntries[heartbeatEntries.length - 1];
  }, [logs]);

  const filteredLogs = useMemo(() => {
    return logs
      .map((log, idx) => ({
        log,
        originalIdx: idx,
        entryId: `${idx}-${log.timestamp}-${log.message}`,
      }))
      .filter(({ log }) => {
        if (isHeartbeatNoise(log)) {
          return false;
        }

        const severity = getLogSeverity(log.message, log.level);
        const logType = getLogType(log.message);

        if (selectedLogTypes.length > 0) {
          const matches = selectedLogTypes.includes(logType);
          if (typeFilterMode === 'include' ? !matches : matches) {
            return false;
          }
        }

        if (selectedSeverities.length > 0) {
          const matches = selectedSeverities.includes(severity);
          if (severityFilterMode === 'include' ? !matches : matches) {
            return false;
          }
        }

        if (searchText && !log.message.toLowerCase().includes(searchText.toLowerCase())) {
          return false;
        }

        return true;
      });
  }, [logs, searchText, selectedLogTypes, selectedSeverities]);

  const scrollToSelectedEntry = useCallback(() => {
    if (!selectedLogId) {
      return;
    }

    const selectedEntry = filteredLogs.find((entry) => entry.entryId === selectedLogId);
    if (selectedEntry && selectedLogRef.current) {
      selectedLogRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [filteredLogs, selectedLogId]);

  useEffect(() => {
    if (!selectedLogId) {
      return;
    }

    const selectedEntry = filteredLogs.find((entry) => entry.entryId === selectedLogId);
    if (selectedEntry && selectedLogRef.current) {
      selectedLogRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [filteredLogs, selectedLogId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target as Node)) {
        setIsTypeDropdownOpen(false);
      }
      if (severityDropdownRef.current && !severityDropdownRef.current.contains(event.target as Node)) {
        setIsSeverityDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.getServerLogs(serverId);
      setLogs(res.data.logs);
    } catch (err) {
      console.error('Failed to load logs:', err);
    } finally {
      setLoading(false);
    }
  }, [serverId]);

  const handleClearLogs = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (window.confirm('Are you sure you want to clear all logs for this server?')) {
      try {
        await api.clearServerLogs(serverId);
        setLogs([]);
        setSelectedLogId(null);
      } catch (err) {
        console.error('Failed to clear logs:', err);
      }
    }
  };

  useEffect(() => {
    if (!isVisible) return;

    loadLogs();

    // Poll for new logs every 2 seconds
    const interval = setInterval(loadLogs, 2000);
    return () => clearInterval(interval);
  }, [serverId, isVisible, loadLogs]);

  return (
    <div className="server-logs">
      <div className="logs-header">
        <h3>Server Logs</h3>
        <div className="logs-actions">
          <div className="search-box">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search logs..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="search-input"
            />
            {searchText && (
              <button
                className="clear-search"
                onClick={() => setSearchText('')}
                title="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>

          <div className="filter-dropdown" ref={typeDropdownRef}>
            <button
              type="button"
              className="filter-dropdown-button"
              onClick={() => setIsTypeDropdownOpen((open) => !open)}
            >
              <span>Type {selectedLogTypes.length > 0 ? `(${selectedLogTypes.length})` : ''}</span>
              <ChevronDown size={14} />
            </button>
            {isTypeDropdownOpen ? (
              <div className="filter-dropdown-menu">
                <div className="filter-mode-toggle">
                  <button
                    type="button"
                    className={typeFilterMode === 'include' ? 'active' : ''}
                    onClick={() => setTypeFilterMode('include')}
                  >
                    Include
                  </button>
                  <button
                    type="button"
                    className={typeFilterMode === 'exclude' ? 'active' : ''}
                    onClick={() => setTypeFilterMode('exclude')}
                  >
                    Exclude
                  </button>
                </div>
                {availableLogTypes.map((type) => {
                  const isSelected = selectedLogTypes.includes(type);
                  return (
                    <button
                      key={type}
                      type="button"
                      className={`filter-option ${isSelected ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedLogTypes((current) => {
                          if (current.includes(type)) {
                            return current.filter((value) => value !== type);
                          }
                          return [...current, type];
                        });
                      }}
                    >
                      {type}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>

          <div className="filter-dropdown" ref={severityDropdownRef}>
            <button
              type="button"
              className="filter-dropdown-button"
              onClick={() => setIsSeverityDropdownOpen((open) => !open)}
            >
              <span>Severity {selectedSeverities.length > 0 ? `(${selectedSeverities.length})` : ''}</span>
              <ChevronDown size={14} />
            </button>
            {isSeverityDropdownOpen ? (
              <div className="filter-dropdown-menu">
                <div className="filter-mode-toggle">
                  <button
                    type="button"
                    className={severityFilterMode === 'include' ? 'active' : ''}
                    onClick={() => setSeverityFilterMode('include')}
                  >
                    Include
                  </button>
                  <button
                    type="button"
                    className={severityFilterMode === 'exclude' ? 'active' : ''}
                    onClick={() => setSeverityFilterMode('exclude')}
                  >
                    Exclude
                  </button>
                </div>
                {availableSeverities.map((severity) => {
                  const isSelected = selectedSeverities.includes(severity);
                  return (
                    <button
                      key={severity}
                      type="button"
                      className={`filter-option ${isSelected ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedSeverities((current) => {
                          if (current.includes(severity)) {
                            return current.filter((value) => value !== severity);
                          }
                          return [...current, severity];
                        });
                      }}
                    >
                      {severity}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>

          <button
            className="logs-button refresh"
            onClick={loadLogs}
            disabled={loading}
            title="Refresh logs"
          >
            <RefreshCw size={16} />
          </button>

          <label className="auto-scroll-label">
            <input
              type="checkbox"
              checked={autoScroll}
              onChange={(e) => setAutoScroll(e.target.checked)}
            />
            Auto-scroll
          </label>

          <button
            className="logs-button clear"
            onClick={handleClearLogs}
            title="Clear all logs"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      <div className="logs-info">
        <div>Showing {filteredLogs.length} of {logs.length} logs</div>
        {lastHeartbeat ? (
          <div className="last-heartbeat">
            Last heartbeat: {new Date(lastHeartbeat.timestamp).toLocaleString()}
          </div>
        ) : null}
      </div>

      <div className="logs-container">
        {filteredLogs.length === 0 ? (
          <div className="logs-empty">
            {logs.length === 0 ? 'No logs yet' : 'No logs match the filters'}
          </div>
        ) : (
          filteredLogs.map(({ log, originalIdx, entryId }) => {
            const isSelected = entryId === selectedLogId;
            return (
              <div
                key={entryId}
                ref={isSelected ? (node) => {
                  selectedLogRef.current = node;
                } : undefined}
                className={`log-entry log-${log.level} ${isSelected ? 'log-selected' : ''}`}
                onClick={() => setSelectedLogId((current) => current === entryId ? null : entryId)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSelectedLogId((current) => current === entryId ? null : entryId);
                  }
                }}
              >
                <span className="log-time">
                  {new Date(log.timestamp).toLocaleTimeString()}
                </span>
                <span className={`log-level log-level-${log.level}`}>
                  {log.level.toUpperCase()}
                </span>
                <span className="log-message">{log.message}</span>
              </div>
            );
          })
        )}
        <div ref={logsEndRef} />
      </div>
    </div>
  );
};

export default ServerLogs;
